import React from 'react';
import { SecaoVendasDrillDownContentProps } from '../../types/secaoVendasDrillDown';
import BaseSecaoDrillDownContent, { 
  BaseSecaoDrillDownData, 
  SecaoDrillDownConfig 
} from '../DrillDown/BaseSecaoDrillDownContent';

/**
 * Configuração específica para drill down de seções de vendas
 * Princípio OCP: Extensível sem modificar código existente
 */
const vendasConfig: SecaoDrillDownConfig = {
  transactionType: 'vendas',
  labels: {
    title: 'Resumo da Seção',
    totalTransaction: 'Total Vendas',
    partners: 'Clientes',
    products: 'Produtos',
    documents: 'Documentos',
    participation: 'Participação',
    ticketMedio: 'Ticket Médio',
    evolutionTitle: 'Evolução das Vendas',
    topPartnersTitle: 'Top 10 Clientes da Seção',
    topProductsTitle: 'Top 10 Produtos da Seção',
    cfopsTitle: 'CFOPs Utilizados',
    ncmTitle: 'Análise por NCM'
  },
  colors: {
    primary: '#1976d2',
    secondary: '#42a5f5',
    success: '#2e7d32',
    warning: '#ed6c02',
    info: '#0288d1'
  },
  valueKeys: {
    transactionValue: 'total_transacao',
    partnerValue: 'valor_transacao',
    productValue: 'valor_transacao',
    evolutionValue: 'valor'
  }
};

/**
 * Adaptador para converter dados de vendas para o formato base
 * Princípio SRP: Responsável apenas pela conversão de dados
 */
const adaptVendasDataToBase = (data: any): BaseSecaoDrillDownData => {
  // Verificações de segurança para evitar erros com dados undefined
  const secao = data?.secao || {};
  const evolucao_mensal = data?.evolucao_mensal || [];
  const top_clientes = data?.top_parceiros || data?.top_clientes || [];
  const principais_produtos = data?.principais_produtos || [];
  const cfops_utilizados = data?.cfops_utilizados || [];

  return {
    secao: {
      nome: secao.nome || 'N/A',
      total_transacao: secao.total_vendas || secao.total_valor || 0,
      participacao_percentual: secao.participacao_percentual || 0,
      total_documentos: secao.total_documentos || 0,
      parceiros_distintos: secao.clientes_distintos || secao.parceiros_distintos || 0,
      produtos_distintos: secao.produtos_distintos || 0,
      quantidade_total: secao.quantidade_total || 0,
      ticket_medio: secao.ticket_medio || 0
    },
    evolucao_mensal: evolucao_mensal.map((item: any) => ({
      periodo: item?.periodo || '',
      valor: item?.vendas || item?.valor || 0,
      documentos: item?.documentos || 0,
      parceiros: item?.clientes || item?.parceiros || 0,
      produtos: item?.quantidade || item?.produtos || 0
    })),
    top_parceiros: top_clientes.map((cliente: any) => ({
      nome: cliente?.nome || 'N/A',
      valor_transacao: cliente?.valor_total || cliente?.valor_vendas || cliente?.valor_transacao || 0,
      valor_total: cliente?.valor_total || cliente?.valor_vendas || cliente?.valor_transacao || 0,
      participacao_percentual: cliente?.participacao_percentual || 0,
      documentos: cliente?.documentos || 0,
      produtos_distintos: cliente?.produtos_distintos || 0
    })),
    principais_produtos: principais_produtos.map((produto: any) => ({
      cod_item: produto?.cod_item || 'N/A',
      descricao_produto: produto?.descricao_produto || '',
      valor_transacao: produto?.valor_total || produto?.valor_vendas || produto?.valor_transacao || 0,
      valor_total: produto?.valor_total || produto?.valor_vendas || produto?.valor_transacao || 0,
      quantidade: produto?.quantidade_vendida || produto?.quantidade || 0
    })),
    cfops_utilizados: cfops_utilizados.map((cfop: any) => ({
      cfop: cfop?.cfop || 'N/A',
      descricao_cfop: cfop?.descricao_cfop || '',
      valor_total: cfop?.valor_total || 0,
      documentos: cfop?.documentos || 0,
      parceiros: cfop?.clientes || cfop?.parceiros || 0
    }))
  };
};

/**
 * Componente para drill down de seções de vendas
 * Princípio SRP: Responsável apenas pela apresentação de drill down de vendas
 * Princípio OCP: Extensível via configuração
 * Princípio DIP: Depende de abstrações (componente base) não de implementações concretas
 */
const SecaoVendasDrillDownContent: React.FC<SecaoVendasDrillDownContentProps> = ({ data }) => {
  // Verificar se os dados existem antes de processar
  if (!data) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        Nenhum dado disponível para exibir
      </div>
    );
  }

  // Converter dados para o formato base
  const baseData = adaptVendasDataToBase(data);

  return (
    <BaseSecaoDrillDownContent 
      data={baseData} 
      config={vendasConfig} 
    />
  );
};

export default SecaoVendasDrillDownContent; 